import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import { CART_SAVINGS_BANNER_DISMISSED } from 'constants/plusMembership';
import { JoinModal } from 'features/paid-membership/JoinModal';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import useOrderMin from 'hooks/useOrderMin';
import { trackEvent } from 'utils/analyticsUtils';
import { getCartMemberSavings, getFreeShippingThresholdMet, PriceType } from 'utils/priceUtils';

/**
 * Promotes M+ membership with estimated cart savings; supports an inline and a sticky (bottom) layout.
 *
 * @param {Object} props
 * @param {boolean} [props.hide=false] - When `sticky`, hides the bar off-screen instead of “sticking”
 * @param {IntersectionObserverEntry | undefined} [props.intersectionEntry] - Drives when the user has scrolled past the banner
 * @param {Ref<HTMLDivElement> | undefined} [props.intersectionRef] - Attached to the non-sticky root for intersection observation
 * @param {boolean} [props.savingsBannerInView=false] - For the non-sticky layout, used with view tracking when the cart column is in view
 * @param {boolean} [props.sticky=false] - Renders the bottom sticky variant (with dismiss)
 * @returns {ReactNode | false} Banner UI, or `false` when hidden
 */
function PlusMembershipSavingsBanner({
    hide = false,
    intersectionEntry,
    intersectionRef,
    savingsBannerInView = false,
    sticky = false,
} = {}) {
    const [modalOpen, setModalOpen] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(localStorage.getItem(CART_SAVINGS_BANNER_DISMISSED));
    const [belowBanner, setBelowBanner] = useState(false);
    const impressionSentRef = useRef(false);

    const { data: account = {} } = useAccountQuery();
    const { data: selections = {} } = useSelectionsQuery();
    const { plusMembershipShortBranding, shippingFees } = useAccountPlusMembershipHook();

    const { isOrderMinMet } = useOrderMin();

    const freeShippingThresholdMet = getFreeShippingThresholdMet({
        account,
        options: { priceType: PriceType.PLUS_MEMBER },
        selections,
    });
    const { plusMembershipShippingSavings } = shippingFees ?? {};
    const freeShippingWithMembership = !freeShippingThresholdMet && plusMembershipShippingSavings > 0;
    const cartMemberShippingSavings = freeShippingWithMembership ? plusMembershipShippingSavings : 0;
    const cartMemberSavings = getCartMemberSavings({ account, selections }) + cartMemberShippingSavings;

    useEffect(() => {
        setBelowBanner(intersectionEntry?.boundingClientRect?.top < 0);
    }, [intersectionEntry]);

    const shouldShow = isOrderMinMet && cartMemberSavings > 0 && !(sticky && (bannerDismissed || belowBanner));

    useEffect(() => {
        if (!shouldShow || impressionSentRef.current) return;
        const isVisible = sticky ? !hide : savingsBannerInView;
        if (!isVisible) return;
        trackEvent('M+ cart savings banner view');
        impressionSentRef.current = true;
    }, [shouldShow, sticky, hide, savingsBannerInView]);

    if (!shouldShow) {
        return false;
    }

    const handleClick = (e) => {
        if (e.type !== 'keydown' || e.code === 'Enter' || e.key === 'Enter' || e.keyCode === 13) {
            setModalOpen(true);
            trackEvent('M+ sticky cart banner click', { sticky });
        }
    };

    const dismissBanner = () => {
        setBannerDismissed(true);
        localStorage.setItem(CART_SAVINGS_BANNER_DISMISSED, true);
        trackEvent('M+ sticky cart banner dismiss');
    };

    const bannerContent = (
        <div
            className="rounded-lg bg-cranberry p-10 text-center text-white if:bg-blueberry"
            onClick={handleClick}
            onKeyDown={handleClick}
            role="button"
            tabIndex={0}
        >
            <p className="text-body-sm leading-[20px]!">
                <span className="text-body-sm block font-grotesk-bold">
                    Want to save an extra ${cartMemberSavings.toFixed(2)} on this order?
                </span>
                <span className="text-body-xs underline">Sign up for {plusMembershipShortBranding} now</span>
            </p>
        </div>
    );

    if (sticky) {
        return (
            <div className={clsx('z-10', hide ? 'overflow-hidden' : 'sticky bottom-20 m-20')}>
                <div className={clsx('transition', hide ? 'translate-y-full' : 'translate-y-0')}>
                    <button
                        type="button"
                        onClick={dismissBanner}
                        className="absolute -top-15 left-10 rounded-full bg-off-white"
                        aria-label="Close banner"
                    >
                        <XMarkIcon className="h-25 w-25 text-black" />
                    </button>
                    {bannerContent}
                    <JoinModal onClose={() => setModalOpen(false)} open={modalOpen} />
                </div>
            </div>
        );
    }

    return (
        <div ref={intersectionRef} className="mt-10 overflow-hidden rounded-lg">
            {bannerContent}
            <JoinModal onClose={() => setModalOpen(false)} open={modalOpen} />
        </div>
    );
}

export { PlusMembershipSavingsBanner };
